import React, { useContext, useState } from "react";
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dropdown, Spinner } from "react-bootstrap";
import { ArrowClockwise } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useAbsence } from "../../../../api/absence";
import { useCongSettings } from "../../../../api/cong";
import useFSGroups from "../../../../api/fsgroup";
import { jobApi } from "../../../../api/job";
import { meetingsApi, useNotifications, usePrivilegeMap } from "../../../../api/meetings";
import { useOtherSchedule } from "../../../../api/otherSchedule";
import QueryKeys from "../../../../api/queryKeys";
import { useWMSchedules, weekendApi } from "../../../../api/weekend";
import { publicWitnessingApi } from "../../../../api/publicWitnessing";
import { HGBugsnagNotify } from "../../../../helpers/bugsnag";
import { dateStringCompare, isBeforeToday, weekOfString } from "../../../../helpers/dateHelpers";
import { getStatusCode } from "../../../../helpers/errors";
import { DateRange, ISODateString, NotificationDates } from "../../../../types/date";
import { JobStatus } from "../../../../types/job";
import { NotificationType } from "../../../../types/scheduling/meetings";
import { OtherScheduleKind } from "../../../../types/scheduling/other";
import { CongSettingTypes } from "../../../../types/scheduling/settings";
import { ActionsDropdown } from "../../../buttons";
import { QueryStatus } from "../../../queryStatus";
import { useAssignmentMap } from "../../RecentAssignments";
import { useUserMap } from "../../common";
import { MonthScheduleMenu } from "../../date";
import { NotificationStatusLegend } from "../../notificationStatus";
import { canUpdateWeekendSchedules, needSendAssignments } from "../wm_common";
import { WeekSchedule } from "./index";
import useInterval from "../../../../helpers/useInterval";
import { SendAssignmentsReminder } from "../../common";
import { assignmentNotificationQueryKey } from "../../../../query/notification";
import { HGContext } from "../../../../helpers/globals";
import { ScheduleOverviewModal } from "./ScheduleOverviewModal";

interface CRHScheduleProps {
  from: ISODateString;
  to: ISODateString;
  langGroupId: number;
  month: Date;
  setMonth: (d: Date) => void;
  monthCount: number;
  setMonthCount: (c: number) => void;
}

export const Schedule: React.FC<CRHScheduleProps> = ({
  from,
  to,
  langGroupId,
  month,
  setMonth,
  monthCount,
  setMonthCount,
}) => {
  const { t } = useTranslation();
  const [showSpinner, setShowSpinner] = useState(false);
  const [CRHJobId, setCRHJobId] = useState("");
  const [ptInJobId, setPTInJobId] = useState("");
  const [ptOutJobId, setPTOutJobId] = useState("");
  const userMap = useUserMap();
  const hostCong = useContext(HGContext).globals.cong;
  const scheduleQuery = useWMSchedules(from, to, langGroupId, { placeholderData: keepPreviousData });
  const userAssignmentMapQuery = usePrivilegeMap(langGroupId);
  const absencesQuery = useAbsence();
  const notificationDates: NotificationDates = { start: weekOfString(from), end: to };
  const scheduleDates: DateRange = { from, to };
  const crhNotificationsQuery = useNotifications(
    notificationDates.start,
    notificationDates.end,
    NotificationType.WeekendCRH,
  );
  const ptInNotificationsQuery = useNotifications(notificationDates.start, notificationDates.end, NotificationType.PT);
  const ptOutNotificationsQuery = useNotifications(
    notificationDates.start,
    notificationDates.end,
    NotificationType.PTOut,
  );
  const queryClient = useQueryClient();
  const autoFillMutation = useMutation({
    mutationFn: (range: DateRange) => weekendApi.autoRangeCRH(range.from, range.to, langGroupId),
  });
  const canUpdate = canUpdateWeekendSchedules();
  const assignmentMap = useAssignmentMap({ from, to }, canUpdate, langGroupId);
  const congSettingsQuery = useCongSettings(langGroupId);
  const fsGroupsQuery = useFSGroups({ enabled: congSettingsQuery.data?.hospitality_by_group });
  // only needed to flag a potential conflict when the same group assigned to hospitality is also assigned to clean
  const cleaningQuery = useOtherSchedule(OtherScheduleKind.Cleaning, from, to, {
    enabled: congSettingsQuery.data?.hospitality_by_group,
  });

  // Queries for the OutgoingSection component
  const outSpeakersQuery = useQuery({
    queryKey: [`${QueryKeys.WMOutSpeakers}-local`, langGroupId],
    queryFn: () => weekendApi.getSpeakers(hostCong?.id ?? 0, true, 0),
  });

  const congregationsQuery = useQuery({
    queryKey: [QueryKeys.WMCongregations, langGroupId],
    queryFn: () => weekendApi.getCongregations(langGroupId),
  });

  const pwScheduleQuery = useQuery({
    queryKey: [QueryKeys.PublicWitnessingSchedules, from, to],
    queryFn: () => publicWitnessingApi.getSchedules(from, to),
    placeholderData: keepPreviousData,
  });

  const [showOverviewModal, setShowOverviewModal] = useState(false);

  useInterval(async () => {
    const jobId = CRHJobId || ptOutJobId || ptInJobId;
    if (!jobId) return;
    try {
      const resp = await jobApi.getJob(jobId);
      switch (resp.status) {
        case JobStatus.Pending:
          return;
        case JobStatus.Done:
        case JobStatus.Failed:
          if (jobId === CRHJobId) setCRHJobId("");
          else if (jobId === ptOutJobId) setPTOutJobId("");
          else if (jobId === ptInJobId) setPTInJobId("");
          setShowSpinner(false);
          break;
      }
      const jobNotificationType = (): NotificationType => {
        if (jobId === ptOutJobId) return NotificationType.PTOut;
        if (jobId === ptInJobId) return NotificationType.PT;
        return NotificationType.WeekendCRH;
      };
      await queryClient.invalidateQueries({
        queryKey: assignmentNotificationQueryKey(jobNotificationType(), notificationDates.start, notificationDates.end),
      });
    } catch (err: any) {
      if (getStatusCode(err) === 404) {
        setShowSpinner(false);
        if (jobId === CRHJobId) setCRHJobId("");
        else if (jobId === ptOutJobId) setPTOutJobId("");
        else if (jobId === ptInJobId) setPTInJobId("");
      } else {
        console.error("wm: error fetching job status", err);
        HGBugsnagNotify("wmFetchJob", err);
      }
    }
  }, 3 * 1000);

  const queryStatus = QueryStatus(
    scheduleQuery,
    userAssignmentMapQuery,
    congSettingsQuery,
    outSpeakersQuery,
    congregationsQuery,
    pwScheduleQuery,
  );
  if (queryStatus !== null) return queryStatus;
  if (
    !scheduleQuery.data ||
    !userAssignmentMapQuery.data ||
    !congSettingsQuery.data ||
    !outSpeakersQuery.data ||
    !congregationsQuery.data ||
    !pwScheduleQuery.data
  )
    return null;

  const autoFill = async () => {
    setShowSpinner(true);
    const range: DateRange = { from, to };
    try {
      await autoFillMutation.mutateAsync(range);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMCSchedules, langGroupId] });
    } catch (err: any) {
      console.error("wm autofill all error", err);
      HGBugsnagNotify("wmAutofill", err);
    } finally {
      setShowSpinner(false);
    }
  };

  const haveLocalPTIn = scheduleQuery.data.some((s) => userMap.has(s.speaker?.userId ?? 0));
  const havePTOut = outSpeakersQuery.data.some((s) => !!s.userId);

  const sendAssignments = async () => {
    setShowSpinner(true);
    try {
      const job = await meetingsApi.sendAllNotifications(
        notificationDates.start,
        notificationDates.end,
        NotificationType.WeekendCRH,
        langGroupId,
      );
      setCRHJobId(job.id);
      await queryClient.invalidateQueries({
        queryKey: assignmentNotificationQueryKey(
          NotificationType.WeekendCRH,
          notificationDates.start,
          notificationDates.end,
        ),
      });

      if (haveLocalPTIn) {
        const ptInJob = await meetingsApi.sendAllNotifications(
          notificationDates.start,
          notificationDates.end,
          NotificationType.PT,
          langGroupId,
        );
        setPTInJobId(ptInJob.id);
        await queryClient.invalidateQueries({
          queryKey: assignmentNotificationQueryKey(NotificationType.PT, notificationDates.start, notificationDates.end),
        });
      }

      if (havePTOut) {
        const ptOutJob = await meetingsApi.sendAllNotifications(
          notificationDates.start,
          notificationDates.end,
          NotificationType.PTOut,
          langGroupId,
        );
        setPTOutJobId(ptOutJob.id);
        await queryClient.invalidateQueries({
          queryKey: assignmentNotificationQueryKey(
            NotificationType.PTOut,
            notificationDates.start,
            notificationDates.end,
          ),
        });
      }
    } catch (err: any) {
      setShowSpinner(false);
      console.error("wm crh: error sending notifications", err);
      HGBugsnagNotify("wmSendAssignments", err);
    }
  };

  return (
    <div>
      {showOverviewModal && (
        <ScheduleOverviewModal
          startMonth={month}
          show={showOverviewModal}
          onHide={() => setShowOverviewModal(false)}
          langGroupId={langGroupId}
          onNavigateToDate={(date) => {
            setShowOverviewModal(false);
            setMonth(new Date(date));
          }}
          congregation={hostCong}
        />
      )}
      <div className="sticky-top bg-body py-2">
        <div className="d-flex justify-content-between">
          <div>
            <ActionsDropdown disabled={!canUpdateWeekendSchedules()}>
              <Dropdown.Menu>
                <Dropdown.Item onClick={autoFill} aria-label={t("schedules.fill.auto")}>
                  <ArrowClockwise /> {t("schedules.fill.auto")}
                </Dropdown.Item>
                {!isBeforeToday(to) && (
                  <Dropdown.Item onClick={sendAssignments} disabled={showSpinner} aria-label={t("schedules.assignment.send")}>
                    {t("schedules.assignment.send")}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </ActionsDropdown>
            {showSpinner && <Spinner className="ms-2" size="sm" animation="border" />}
          </div>
          <MonthScheduleMenu
            date={month}
            setDate={setMonth}
            dateSaveKey={CongSettingTypes.WM}
            months={monthCount}
            setMonths={setMonthCount}
            monthsMenuContent={
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setShowOverviewModal(true)}>{t("general.overview")}</Dropdown.Item>
              </>
            }
          />
          <div />
        </div>
      </div>
      {needSendAssignments(langGroupId, crhNotificationsQuery.data, scheduleQuery.data) && (
        <SendAssignmentsReminder className="w-75" />
      )}
      <div className="d-flex flex-column gap-4">
        {scheduleQuery.data
          .sort((a, b) => dateStringCompare(a.date, b.date))
          .map((schedule) => (
            <WeekSchedule
              key={`week${schedule.date}`}
              schedule={schedule}
              langGroupId={langGroupId}
              refresh={scheduleQuery.refetch}
              userPrivilegeMap={userAssignmentMapQuery.data}
              userMap={userMap}
              notificationDates={notificationDates}
              crhNotifications={crhNotificationsQuery.data}
              ptInNotifications={ptInNotificationsQuery.data}
              ptOutNotifications={ptOutNotificationsQuery.data}
              scheduleDates={scheduleDates}
              assignmentMap={assignmentMap}
              absences={absencesQuery.data}
              congSettings={congSettingsQuery.data}
              fsGroups={fsGroupsQuery.data}
              cleaningSchedules={cleaningQuery.data}
              speakers={outSpeakersQuery.data}
              congregations={congregationsQuery.data}
              pwSchedules={pwScheduleQuery.data}
            />
          ))}
      </div>
      <div className="mt-4 ms-2">
        <NotificationStatusLegend />
      </div>
    </div>
  );
};
